"use client";
import React, { useState } from "react";

import { Card } from "./ui/card";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import Pay from "../components/PayTab";
import Receive from "./ReceiveTab";
import ConfirmationDetails from "./confirmDetails";
import PaymentComplete from "../components/paymentDone";
import PaymentFailed from "../components/paymentFailed";
import PaymentDetails from "../components/paymentDetails";
import { SecuredFooter } from "./ui/secured-footer";
import { useHas88PayHistory } from "../hooks/query/queries";
import EightyEightPayCarousel from "./EightyEightPayCarousel";

export default function EightyEightPay({ userId, initialPayId }) {
  const [selectedTab, setSelectedTab] = useState("pay");
  const [showSecondCard, setShowSecondCard] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(true);

  // Check if user has any 88Pay transactions via dedicated API endpoint
  const { data: has88PayHistory = false, isLoading: isLoadingHistory } =
    useHas88PayHistory(userId);

  const [paymentData, setPaymentData] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [parentAmount, setParentAmount] = useState(null);
  const [parentCurrentAmount, setParentCurrentAmount] = useState(null);
  const [address, setAddress] = useState(null);
  const [sendInfo, setSendInfo] = useState(null);
  const [showFinalPage, setShowFinalPage] = useState(false);
  const [showFailedPage, setShowFailedPage] = useState(false);
  const [showDetailsPage, setShowDetailsPage] = useState(false);
  const [newPayment, setNewPayment] = useState([]);
  const [payFormKey, setPayFormKey] = useState(0);

  const handleContinue = (data) => {
    setPaymentData(data);
    setShowSecondCard(true);
  };

  const handleInfomation = (info) => {
    setSendInfo(info);
  };

  const handleTabChange = (value) => {
    setSelectedTab(value);
  };

  const handlePaymentDone = () => {
    setShowFinalPage(false);
    setShowSecondCard(false);
    setPaymentData([]);
    setSendInfo(null);
    setParentAmount(null);
    setParentCurrentAmount(null);
    setAddress(null);
    setNewPayment([]);
    setPayFormKey((k) => k + 1);
  };

  const handleDetailsComplete = () => {
    setShowDetailsPage(false);
    setShowFinalPage(false);
  };

  const payData = {
    senderId: sendInfo?.userId,
    receiverEmail: sendInfo?.email || null,
    receiverPayId: sendInfo?.payId || null,
    receiverPhone: sendInfo?.phone || null,
    serviceFee: 0,
    amount: paymentData?.amount,
    totalAmount: paymentData?.totalAmount,
    assetId: sendInfo?.assetId,
  };

  const confirmData = {
    amount: paymentData?.displayAmount ?? paymentData?.formattedAmount,
    totalAmount:
      paymentData?.displayTotalAmount ?? paymentData?.formattedTotalAmount,
    amountSecondary: paymentData?.amountSecondary,
    amountDenomination: paymentData?.amountDenomination,
    serviceFee: 0,
  };

  const handlePay = (newPayment) => {
    setNewPayment(newPayment);
    setShowFinalPage(true);
    setShowFailedPage(false);
  };

  const handlePaymentFailed = () => {
    setShowFailedPage(true);
    setShowFinalPage(false);
  };

  const handleTryAgain = () => {
    // Go back to confirmation page to try again
    setShowFailedPage(false);
  };

  const handleGetStarted = () => {
    setShowEmptyState(false);
  };

  // Show empty state if no 88Pay history and user hasn't dismissed it
  // Don't show empty state while loading to prevent flash
  const shouldShowEmptyState =
    !isLoadingHistory && !has88PayHistory && showEmptyState;

  return (
    <>
      {!showSecondCard &&
        !showFinalPage &&
        !showFailedPage &&
        !showDetailsPage && (
          <Card className="flex flex-col w-full !shadow-none bg-white rounded-2xl border border-[#EBE9EF]">
            {shouldShowEmptyState ? (
              <EightyEightPayCarousel onGetStarted={handleGetStarted} />
            ) : (
              <>
                <div className="lg:px-8 px-5 lg:pt-8 pt-5">
                  <Tabs
                    defaultValue="pay"
                    onValueChange={handleTabChange}
                    className="mb-8"
                  >
                    <TabsList className="grid grid-cols-2 rounded-full bg-transparent border border-[#EBE9EF] border-[1px] h-[64px] px-[8px]">
                      <TabsTrigger
                        value="pay"
                        className={`h-[48px] lg:text-lg text-[14px] font-semibold rounded-full transition-colors focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[#33125F] data-[state=active]:text-white ${
                          selectedTab === "pay"
                            ? "bg-[#33125F] text-white"
                            : "text-[#6D6584] bg-transparent"
                        }`}
                      >
                        Pay
                      </TabsTrigger>
                      <TabsTrigger
                        value="receive"
                        className={`h-[48px] lg:text-lg text-[14px] font-semibold rounded-full transition-colors focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[#33125F] data-[state=active]:text-white ${
                          selectedTab === "receive"
                            ? "bg-[#33125F] text-white"
                            : "text-[#6D6584] bg-transparent"
                        }`}
                      >
                        Receive
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  {selectedTab === "pay" ? (
                    <Pay
                      key={payFormKey}
                      userId={userId}
                      onSelect={handleContinue}
                      onConfirm={handleInfomation}
                      setSelectedAsset={setSelectedAsset}
                      selectedAsset={selectedAsset}
                      parentAmount={parentAmount}
                      setParentAmount={setParentAmount}
                      address={address}
                      setAddress={setAddress}
                      setParentCurrentAmount={setParentCurrentAmount}
                      parentCurrentAmount={parentCurrentAmount}
                      initialPayId={initialPayId}
                    />
                  ) : (
                    <Receive />
                  )}
                </div>
                <SecuredFooter />
              </>
            )}
          </Card>
        )}
      {showSecondCard && !showFinalPage && !showFailedPage && (
        <ConfirmationDetails
          confirmData={confirmData}
          paymentPayData={payData}
          onContinue={handlePay}
          onFail={handlePaymentFailed}
        />
      )}
      {showFinalPage && !showFailedPage && (
        <PaymentComplete onDone={handlePaymentDone} />
      )}
      {showFailedPage && <PaymentFailed onTryAgain={handleTryAgain} />}
      {showDetailsPage && !showFinalPage && (
        <PaymentDetails
          transactionData={newPayment}
          onDone={handleDetailsComplete}
        />
      )}
    </>
  );
}
